import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Scale,
  Ruler,
  Camera,
  TrendingDown,
  TrendingUp,
  Upload,
  Calendar,
  Image,
  Loader2,
  GitCompare
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function Progress() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [newMetrics, setNewMetrics] = useState({
    weight: "",
    waist: "",
    hips: "",
    chest: "",
    body_fat: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [metricsRes, photosRes] = await Promise.all([
        axios.get(`${API}/metrics`),
        axios.get(`${API}/photos`)
      ]);
      setMetrics(metricsRes.data.metrics || []);
      setPhotos(photosRes.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogMetrics = async (e) => {
    e.preventDefault();
    
    const hasData = Object.values(newMetrics).some(v => v !== "");
    if (!hasData) {
      toast.error("Please enter at least one measurement");
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = {};
      Object.entries(newMetrics).forEach(([key, value]) => {
        if (value !== "") {
          dataToSend[key] = key === "notes" ? value : parseFloat(value);
        }
      });
      
      await axios.post(`${API}/metrics`, dataToSend);
      toast.success("Metrics logged successfully!");
      setNewMetrics({ weight: "", waist: "", hips: "", chest: "", body_fat: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to log metrics");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.post(`${API}/photos`, {
            photo_type: type,
            image_data: reader.result
          });
          toast.success("Photo uploaded successfully!");
          fetchData();
        } catch (error) {
          toast.error("Failed to upload photo");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploading(false);
      toast.error("Failed to process image");
    }
  };

  const chartData = metrics.slice().reverse().map(m => ({
    date: new Date(m.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    weight: m.weight,
    waist: m.waist
  }));

  const latestMetrics = metrics[0] || {};
  const previousMetrics = metrics[1] || {};
  const weightChange = latestMetrics.weight && previousMetrics.weight 
    ? (latestMetrics.weight - previousMetrics.weight).toFixed(1) 
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">PROGRESS</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Tabs defaultValue="metrics" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="metrics" data-testid="progress-metrics-tab" className="uppercase font-bold tracking-wider">
              Metrics
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="progress-photos-tab" className="uppercase font-bold tracking-wider">
              Photos
            </TabsTrigger>
          </TabsList>

          {/* Metrics Tab */}
          <TabsContent value="metrics">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Log New Metrics */}
              <Card className="bg-card border-border lg:col-span-1">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Scale className="h-5 w-5 text-primary" />
                    Log Today
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogMetrics} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="weight" className="text-xs uppercase tracking-wider">Weight (lbs)</Label>
                        <Input
                          id="weight"
                          type="number"
                          step="0.1"
                          data-testid="metrics-weight-input"
                          value={newMetrics.weight}
                          onChange={(e) => setNewMetrics(prev => ({ ...prev, weight: e.target.value }))}
                          placeholder="185"
                          className="bg-input border-transparent focus:border-primary rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="waist" className="text-xs uppercase tracking-wider">Waist (in)</Label>
                        <Input
                          id="waist"
                          type="number"
                          step="0.1"
                          data-testid="metrics-waist-input"
                          value={newMetrics.waist}
                          onChange={(e) => setNewMetrics(prev => ({ ...prev, waist: e.target.value }))}
                          placeholder="34"
                          className="bg-input border-transparent focus:border-primary rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="hips" className="text-xs uppercase tracking-wider">Hips (in)</Label>
                        <Input
                          id="hips"
                          type="number"
                          step="0.1"
                          data-testid="metrics-hips-input"
                          value={newMetrics.hips}
                          onChange={(e) => setNewMetrics(prev => ({ ...prev, hips: e.target.value }))}
                          placeholder="38"
                          className="bg-input border-transparent focus:border-primary rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="chest" className="text-xs uppercase tracking-wider">Chest (in)</Label>
                        <Input
                          id="chest"
                          type="number"
                          step="0.1"
                          data-testid="metrics-chest-input"
                          value={newMetrics.chest}
                          onChange={(e) => setNewMetrics(prev => ({ ...prev, chest: e.target.value }))}
                          placeholder="42"
                          className="bg-input border-transparent focus:border-primary rounded-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="body_fat" className="text-xs uppercase tracking-wider">Body Fat % (optional)</Label>
                      <Input
                        id="body_fat"
                        type="number"
                        step="0.1"
                        data-testid="metrics-bodyfat-input"
                        value={newMetrics.body_fat}
                        onChange={(e) => setNewMetrics(prev => ({ ...prev, body_fat: e.target.value }))}
                        placeholder="20"
                        className="bg-input border-transparent focus:border-primary rounded-sm"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      data-testid="metrics-submit-btn"
                      disabled={saving}
                      className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log Metrics"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Chart */}
              <Card className="bg-card border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter">Progress Chart</CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.length > 1 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="date" stroke="#666" fontSize={12} />
                          <YAxis stroke="#666" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1A1A1A', 
                              border: '1px solid #333',
                              borderRadius: '2px'
                            }} 
                          />
                          <Line type="monotone" dataKey="weight" stroke="#DC143C" strokeWidth={2} dot={{ fill: '#DC143C' }} name="Weight (lbs)" />
                          <Line type="monotone" dataKey="waist" stroke="#00FF94" strokeWidth={2} dot={{ fill: '#00FF94' }} name="Waist (in)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      Log more metrics to see your progress chart
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Current Stats */}
              <Card className="bg-card border-border lg:col-span-3">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter">Current Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                    <div className="p-4 bg-secondary rounded-sm text-center">
                      <div className="text-3xl font-black text-primary font-mono">
                        {latestMetrics.weight || '--'}
                      </div>
                      <div className="text-sm text-muted-foreground uppercase tracking-wider">Weight (lbs)</div>
                      {weightChange && (
                        <div className={`flex items-center justify-center gap-1 mt-1 text-sm ${parseFloat(weightChange) < 0 ? 'text-accent' : 'text-orange-500'}`}>
                          {parseFloat(weightChange) < 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                          {weightChange} lbs
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-secondary rounded-sm text-center">
                      <div className="text-3xl font-black text-accent font-mono">
                        {latestMetrics.waist || '--'}
                      </div>
                      <div className="text-sm text-muted-foreground uppercase tracking-wider">Waist (in)</div>
                    </div>
                    <div className="p-4 bg-secondary rounded-sm text-center">
                      <div className="text-3xl font-black text-blue-400 font-mono">
                        {latestMetrics.hips || '--'}
                      </div>
                      <div className="text-sm text-muted-foreground uppercase tracking-wider">Hips (in)</div>
                    </div>
                    <div className="p-4 bg-secondary rounded-sm text-center">
                      <div className="text-3xl font-black text-purple-400 font-mono">
                        {latestMetrics.chest || '--'}
                      </div>
                      <div className="text-sm text-muted-foreground uppercase tracking-wider">Chest (in)</div>
                    </div>
                    <div className="p-4 bg-secondary rounded-sm text-center">
                      <div className="text-3xl font-black text-orange-400 font-mono">
                        {latestMetrics.body_fat || '--'}%
                      </div>
                      <div className="text-sm text-muted-foreground uppercase tracking-wider">Body Fat</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Metrics History */}
              {metrics.length > 0 && (
                <Card className="bg-card border-border lg:col-span-3">
                  <CardHeader>
                    <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                            <th className="text-center py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Weight</th>
                            <th className="text-center py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Waist</th>
                            <th className="text-center py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Hips</th>
                            <th className="text-center py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Chest</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metrics.slice(0, 10).map((m, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-3 px-3 text-sm">{new Date(m.timestamp).toLocaleDateString()}</td>
                              <td className="py-3 px-3 text-sm text-center font-mono">{m.weight || '-'}</td>
                              <td className="py-3 px-3 text-sm text-center font-mono">{m.waist || '-'}</td>
                              <td className="py-3 px-3 text-sm text-center font-mono">{m.hips || '-'}</td>
                              <td className="py-3 px-3 text-sm text-center font-mono">{m.chest || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Upload Photos */}
              <Card className="bg-card border-border lg:col-span-3">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Camera className="h-5 w-5 text-primary" />
                    Upload Progress Photos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {['front', 'side', 'back'].map((type) => (
                      <div key={type} className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          id={`photo-${type}`}
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(e, type)}
                          disabled={uploading}
                        />
                        <label
                          htmlFor={`photo-${type}`}
                          className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border hover:border-primary rounded-sm cursor-pointer transition-colors"
                        >
                          {uploading ? (
                            <Loader2 className="h-10 w-10 text-primary animate-spin" />
                          ) : (
                            <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                          )}
                          <span className="text-sm text-muted-foreground uppercase tracking-wider">{type} View</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    Photos are private and only visible to you. Max 5MB per image.
                  </p>
                </CardContent>
              </Card>

              {/* Photo Gallery */}
              {photos.length > 0 ? (
                <Card className="bg-card border-border lg:col-span-3">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                      <Image className="h-5 w-5 text-primary" />
                      Your Progress Photos
                    </CardTitle>
                    <Link to="/compare">
                      <Button variant="outline" size="sm" className="uppercase font-bold tracking-widest rounded-sm">
                        <GitCompare className="mr-2 h-4 w-4" /> Before/After
                      </Button>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {photos.map((photo, i) => (
                        <motion.div
                          key={photo.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                          className="relative aspect-square bg-secondary rounded-sm overflow-hidden group"
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Image className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                            <div className="text-xs uppercase tracking-wider text-white">{photo.photo_type}</div>
                            <div className="text-xs text-white/60">Day {photo.day}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-card border-border lg:col-span-3">
                  <CardContent className="p-12 text-center">
                    <Camera className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-bold uppercase tracking-tighter mb-2">No Photos Yet</h3>
                    <p className="text-muted-foreground">
                      Start documenting your transformation by uploading progress photos.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
